import React from 'react';

import { useAppContext } from '@prisjakt/ui-app-context';

import { Badge } from '@prisjakt/ui/badge';
import Currency from '@prisjakt/ui-currency';
import { IconChevronRight, IconCheck } from '@prisjakt/ui/icon';
import { PrimaryButton, ButtonIcon } from '@prisjakt/ui/button/variants';
import StoreLogo from '@prisjakt/ui-store-logo';
import {
  CaptionText,
  BodySmallText,
  FontEmphasis,
} from '@prisjakt/ui/text/variants';

import { BestOfferShopData } from 'src/types';
import { CheapestOfferPrice } from './types';

import { StyledIconWrapper, StyledStoreWrapper } from './styles';

const BADGE_COLOR = 'GREEN';
const LOGO_WIDTH = 68;

type Props = {
  cheapestPrice?: CheapestOfferPrice;
  onClickHandler: (event: React.MouseEvent) => void;
  priceId?: string | null;
  stockStatus?: string | null;
  store?: BestOfferShopData | null;
  storeLink?: string | null;
};

export const StoreInfo = (props: Props) => {
  const {
    cheapestPrice,
    onClickHandler,
    priceId,
    stockStatus,
    store,
    storeLink,
  } = props;

  const { t } = useAppContext();

  if (!cheapestPrice || !cheapestPrice.exclShipping || !priceId) {
    return null;
  }

  const hasFreeShipping =
    cheapestPrice?.exclShipping === cheapestPrice?.inclShipping;

  const formatPrice = (priceToFormat?: number | null) => {
    if (!priceToFormat) return null;
    // Data for price contains two addtional zeros.
    return priceToFormat / 100;
  };

  return (
    <StyledStoreWrapper>
      {stockStatus && (
        <Badge
          text={t('product', 'price-badge.cheapest-in-stock')}
          icon={() => (
            <StyledIconWrapper>
              <IconCheck size={24} color="positive700" />
            </StyledIconWrapper>
          )}
          color={BADGE_COLOR}
          style={{ whiteSpace: 'nowrap' }}
        />
      )}

      <PrimaryButton
        container="a"
        containerProps={{
          href: storeLink || '',
          rel: 'nofollow',
          target: '_blank',
        }}
        onClick={onClickHandler}
      >
        <BodySmallText emphasis={FontEmphasis.HIGH}>
          <Currency price={formatPrice(cheapestPrice.exclShipping)} />
        </BodySmallText>
        <ButtonIcon>
          <IconChevronRight />
        </ButtonIcon>
      </PrimaryButton>

      {store?.id && (
        <StoreLogo
          name={store.name || ''}
          storeId={store.id}
          featured
          hasImage
          width={LOGO_WIDTH}
        />
      )}

      {cheapestPrice.inclShipping && (
        <CaptionText>
          {hasFreeShipping
            ? t('product', 'price.free-shipping')
            : t('product', 'price.including-shipping', {
                price: (
                  <Currency
                    key={`currency-${priceId}`}
                    price={formatPrice(cheapestPrice?.inclShipping)}
                  />
                ),
              })}
        </CaptionText>
      )}
    </StyledStoreWrapper>
  );
};
